import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import {
    mdiCheck,
    mdiClose,
    mdiOpenInNew,
    mdiAlertCircleOutline,
    mdiCloseCircleOutline,
} from '@mdi/js'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Textarea } from '../ui/textarea'
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { useApproveAssets, useDocument } from '../../hooks/useQueries'
import { useFormatters } from '../../lib/format'

/**
 * AssetApprovalBanner — surfaces the asset-level approval state that used to be
 * completely invisible on the detail page (spec 2026-07-30-02).
 *
 * Three states, keyed off `flags`:
 *  - `to_approve`        → amber banner, provenance line, Approve / Reject.
 *  - `approval_rejected` → muted banner, who rejected it and when. No buttons:
 *                          POST /assets/approve skips any UID not currently in
 *                          `to_approve`, so an Approve button here would
 *                          silently no-op into `skipped`.
 *  - neither             → nothing at all; a confirmed asset's page is
 *                          unchanged.
 *
 * The provenance line is deliberately self-contained rather than delegating to
 * PropertySourceBadge: that component renders a *popover* chip, while this
 * banner must show the evidence (document, page, quote, confidence) inline —
 * it is what the user reads to decide whether to approve.
 */

// Localized labels for the `sources.code` values an asset can be stamped with.
// Same key set PropertySourceBadge uses, so one source reads the same wherever
// it appears.
const SOURCE_LABEL_KEYS: Record<string, string> = {
    manual: 'detail.source_manual',
    document_extraction: 'detail.source_document',
    derivation: 'detail.source_computed',
    propagation: 'detail.source_inherited',
    import: 'detail.source_import',
    aggregation: 'detail.source_aggregated',
    compliance: 'detail.source_compliance',
}

export interface AssetApprovalBannerProps {
    org: string
    assetUid: string
    flags?: string[]
    sourceCode?: string
    sourceDetails?: Record<string, any> | null
    /** jobs.uid owning `source_details.job_execution_uid` (include=source). */
    sourceJobUid?: string | null
    approvedAt?: string | null
    approvedByName?: string | null
    /** Free-form reason recorded when the proposal was rejected. */
    approvalReason?: string | null
    /**
     * How many documents filed on this asset stopped at the entity-extraction
     * hard gate waiting for it to be validated (spec 2026-09-16-02 §4). Says
     * what approving the proposal would unlock; hidden when 0 or absent.
     */
    entityExtractionPendingDocumentCount?: number | null
}

export function AssetApprovalBanner({
    org,
    assetUid,
    flags,
    sourceCode,
    sourceDetails,
    sourceJobUid,
    approvedAt,
    approvedByName,
    approvalReason,
    entityExtractionPendingDocumentCount,
}: AssetApprovalBannerProps) {
    const { t } = useTranslation('assets')
    const { formatDate } = useFormatters()
    const approveAssets = useApproveAssets(org)

    const [rejectOpen, setRejectOpen] = useState(false)
    const [reason, setReason] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    const pending = flags?.includes('to_approve') ?? false
    const rejected = flags?.includes('approval_rejected') ?? false

    const submit = async (action: 'approve' | 'reject', rejectReason?: string) => {
        setError(null)
        setBusy(true)
        try {
            const result: any = await approveAssets.mutateAsync({
                uids: [assetUid],
                action,
                reason: rejectReason,
            })
            // A 200 is not proof of success: the batch endpoint routes anything
            // it will not act on into `skipped`. If this asset landed there and
            // nothing was updated, someone else resolved it first — report the
            // race rather than pretending it worked. The mutation's onSuccess
            // already invalidated the asset query, so the page refetches into
            // the real current state.
            const skipped: string[] = result?.skipped ?? []
            if ((result?.updated ?? 0) === 0 && skipped.includes(assetUid)) {
                setError(t('approval.error_already_resolved'))
                return false
            }
            return true
        } catch {
            setError(t('approval.error_generic'))
            return false
        } finally {
            setBusy(false)
        }
    }

    if (!pending && !rejected) {
        return null
    }

    if (rejected) {
        const when = approvedAt ? formatDate(approvedAt, { year: 'numeric', month: 'short', day: 'numeric' }) : null
        return (
            <div
                className="bg-muted border border-border rounded-md p-3 flex items-start gap-2 text-muted-foreground"
                data-testid="asset-approval-rejected-banner"
            >
                <AppIcon path={mdiCloseCircleOutline} size="md" className="mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                    <p className="text-sm font-medium text-foreground">{t('approval.rejected_title')}</p>
                    {when && (
                        <p className="text-xs">
                            {approvedByName
                                ? t('approval.rejected_at_by', { date: when, name: approvedByName })
                                : t('approval.rejected_at', { date: when })}
                        </p>
                    )}
                    {approvalReason && (
                        <p className="text-xs" data-testid="asset-approval-reason">
                            {t('approval.rejected_reason', { reason: approvalReason })}
                        </p>
                    )}
                    <ProvenanceLine
                        org={org}
                        sourceCode={sourceCode}
                        sourceDetails={sourceDetails}
                        sourceJobUid={sourceJobUid}
                    />
                </div>
            </div>
        )
    }

    // Same amber background as DocumentPendingApprovals. The tinted border and
    // icon carry the warning at a glance — at those opacities the background
    // alone is nearly invisible, especially in dark mode.
    return (
        <div
            className="border border-amber-300/70 dark:border-amber-700/40 rounded-md p-3 bg-amber-50/30 dark:bg-amber-900/10"
            data-testid="asset-approval-banner"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-2 min-w-0">
                    <AppIcon path={mdiAlertCircleOutline} size="md" className="mt-0.5 flex-shrink-0 text-amber-600 dark:text-amber-500" />
                    <div className="space-y-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground">{t('approval.pending_title')}</p>
                        <p className="text-xs text-muted-foreground">{t('approval.pending_description')}</p>
                        {(entityExtractionPendingDocumentCount ?? 0) > 0 && (
                            <p
                                className="text-xs text-muted-foreground"
                                data-testid="asset-approval-pending-extractions"
                            >
                                {t('approval.pending_entity_extractions', {
                                    count: entityExtractionPendingDocumentCount ?? 0,
                                })}
                            </p>
                        )}
                        <ProvenanceLine
                            org={org}
                            sourceCode={sourceCode}
                            sourceDetails={sourceDetails}
                            sourceJobUid={sourceJobUid}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                        variant="default"
                        size="sm"
                        disabled={busy}
                        onClick={() => void submit('approve')}
                        aria-label={t('approval.approve')}
                        title={t('approval.approve')}
                        data-testid="asset-approve-btn"
                    >
                        <AppIcon path={mdiCheck} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('approval.approve')}</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                            setReason('')
                            setError(null)
                            setRejectOpen(true)
                        }}
                        aria-label={t('approval.reject')}
                        title={t('approval.reject')}
                        data-testid="asset-reject-btn"
                    >
                        <AppIcon path={mdiClose} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{t('approval.reject')}</span>
                    </Button>
                </div>
            </div>

            {/* Banner-level error line — the Approve path. While the reject
                dialog is open it sits behind the AlertDialog's opaque overlay,
                so the dialog renders its own copy (below) instead. */}
            {error && !rejectOpen && (
                <p className="mt-2 text-xs text-destructive" data-testid="asset-approval-error">
                    {error}
                </p>
            )}

            <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
                <AlertDialogContent data-testid="asset-reject-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('approval.reject_dialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('approval.reject_dialog.description')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-1">
                        <label className="text-sm text-muted-foreground" htmlFor="asset-reject-reason">
                            {t('approval.reject_dialog.reason_label')}
                        </label>
                        <Textarea
                            id="asset-reject-reason"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            placeholder={t('approval.reject_dialog.reason_placeholder')}
                            data-testid="asset-reject-reason"
                        />
                    </div>
                    {error && (
                        <p className="text-xs text-destructive" data-testid="asset-reject-error">
                            {error}
                        </p>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="asset-reject-cancel">
                            {t('approval.reject_dialog.cancel')}
                        </AlertDialogCancel>
                        <Button
                            variant="destructive"
                            disabled={busy}
                            onClick={async () => {
                                const ok = await submit('reject', reason.trim() || undefined)
                                // Keep the dialog open on failure — the error
                                // renders inside it (the banner-level line
                                // would be hidden behind the overlay), and the
                                // typed reason survives for a retry.
                                if (ok) setRejectOpen(false)
                            }}
                            data-testid="asset-reject-confirm"
                        >
                            {t('approval.reject_dialog.confirm')}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}

/**
 * The provenance line. Every field below `source_code` is optional and
 * extraction rows in the wild are sparse, so each branch degrades on its own
 * and the buttons above stay enabled no matter how little is known — an
 * unexplained proposal must still be approvable.
 */
function ProvenanceLine({
    org,
    sourceCode,
    sourceDetails,
    sourceJobUid,
}: {
    org: string
    sourceCode?: string
    sourceDetails?: Record<string, any> | null
    sourceJobUid?: string | null
}) {
    const { t } = useTranslation('assets')

    const details = sourceDetails ?? {}
    // `ai_extraction` is a legacy alias for `document_extraction` still present
    // on historical rows — normalize it the same way PropertySourceBadge does.
    const rawSource = sourceCode || (typeof details.source === 'string' ? details.source : '')
    const source = rawSource === 'ai_extraction' ? 'document_extraction' : rawSource

    const documentUid: string | undefined =
        typeof details.document_uid === 'string' ? details.document_uid : undefined
    const page: number | undefined = typeof details.page === 'number' ? details.page : undefined
    const quote: string | undefined =
        typeof details.quote === 'string' && details.quote.trim() ? details.quote : undefined
    const confidence: number | undefined =
        typeof details.confidence === 'number' ? details.confidence : undefined
    const jobExecutionUid: string | undefined =
        typeof details.job_execution_uid === 'string' ? details.job_execution_uid : undefined

    const sourceLabel = source
        ? SOURCE_LABEL_KEYS[source]
            ? t(SOURCE_LABEL_KEYS[source])
            : source
        : ''

    const isExtraction = source === 'document_extraction'
    const hasAnything =
        !!source || !!documentUid || page !== undefined || !!quote || confidence !== undefined || !!jobExecutionUid

    if (!hasAnything) {
        return (
            <p className="text-xs text-muted-foreground italic" data-testid="asset-approval-origin-unknown">
                {t('approval.origin_unknown')}
            </p>
        )
    }

    return (
        <div className="space-y-1 text-xs" data-testid="asset-approval-provenance">
            <div className="flex items-center gap-2 flex-wrap">
                {isExtraction && documentUid ? (
                    <SourceDocumentLink org={org} docUid={documentUid} page={page} />
                ) : (
                    <span className="text-muted-foreground">
                        {sourceLabel
                            ? t('approval.created_by_source', { source: sourceLabel })
                            : t('approval.origin_unknown')}
                    </span>
                )}
                {page !== undefined && !(isExtraction && documentUid) && (
                    <span className="text-muted-foreground">{t('approval.page', { page })}</span>
                )}
                {confidence !== undefined && (
                    <Badge variant="secondary" data-testid="asset-approval-confidence">
                        {t('approval.confidence', { value: Math.round(confidence * 100) })}
                    </Badge>
                )}
            </div>
            {quote && (
                <blockquote
                    className="border-l-2 border-primary/40 pl-2 italic text-muted-foreground line-clamp-2"
                    title={quote}
                    data-testid="asset-approval-quote"
                >
                    &ldquo;{quote}&rdquo;
                </blockquote>
            )}
            {jobExecutionUid && sourceJobUid && (
                <Link
                    to="/$org/jobs/$jobUid/executions/$executionUid"
                    params={{ org, jobUid: sourceJobUid, executionUid: jobExecutionUid }}
                    search={{} as never}
                    className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground hover:underline"
                    data-testid="asset-approval-job-link"
                >
                    {t('approval.view_job')}
                    <AppIcon path={mdiOpenInNew} size="xs" className="flex-shrink-0" />
                </Link>
            )}
        </div>
    )
}

/**
 * Link to the document the asset was extracted from. `source_details` carries
 * only the uid, so the name comes from the document-detail query — and the link
 * falls back to a generic label while that loads, or forever if the document
 * was deleted (source_details is a plain JSONB map with no FK).
 */
function SourceDocumentLink({ org, docUid, page }: { org: string; docUid: string; page?: number }) {
    const { t } = useTranslation('assets')
    const { data: document } = useDocument(org, docUid)
    const name = document?.name

    return (
        <Link
            to="/$org/documents/$docUid"
            params={{ org, docUid }}
            search={(page !== undefined ? { page } : {}) as never}
            className="inline-flex items-center gap-1 text-primary hover:underline min-w-0"
            data-testid="asset-approval-document-link"
        >
            <span className="truncate">
                {name
                    ? t('approval.created_from_document', { name })
                    : t('approval.created_from_document_unnamed')}
                {page !== undefined ? ` · ${t('approval.page', { page })}` : ''}
            </span>
            <AppIcon path={mdiOpenInNew} size="xs" className="flex-shrink-0" />
        </Link>
    )
}
